// "Send to the group text": the phone's own share sheet, so it goes from the payer's (or friend's)
// own number to their own group. We never text anyone and never ask for a phone number.
// No share sheet (most desktops): copy the message instead. Never blocks paying.
import { unnamedVenue } from '../venue-name.ts';
import { toast } from './dom.ts';
import { trackStep } from './track.ts';
import type { PublicSplit } from '../types.ts';
import { joinLink, loopLink } from '../loop.ts';

export const groupShareLink = (code: string, payerRef?: string): string => location.origin + joinLink(code, payerRef, 'group_share', 'group');

type Shareable = Pick<PublicSplit, 'venue' | 'code' | 'payerRef'>;

export function groupShareText(s: Shareable): string {
  const venue = unnamedVenue(s.venue) ? '' : s.venue.trim();
  return `Here's our split${venue ? ` for ${venue}` : ''}. Tap what you had and pay me back: ${groupShareLink(s.code, s.payerRef)}`;
}

/**
 * The friend's "Save this for next time": a fresh "Start a split" link, not the dinner. It carries
 * from=<this dinner> and src=group_share, so a new payer from it is table_loop within 7 days of the
 * dinner and group_thread after that (see src/attribution.ts).
 */
export const nextTimeLink = (s: Shareable): string => location.origin + loopLink(s.code, s.payerRef, 'group_share');
export const nextTimeText = (s: Shareable): string => `Next time one of us gets the bill, split it here. No app needed: ${nextTimeLink(s)}`;

export async function groupShare(s: Shareable, role: 'payer' | 'friend'): Promise<void> {
  trackStep('group_share_tapped', { code: s.code, role });
  const text = role === 'friend' ? nextTimeText(s) : groupShareText(s);
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ text });
      trackStep('group_share_completed', { code: s.code, role, how: 'share' });
    } catch { /* closed the share sheet: nothing sent */ }
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const t = document.createElement('textarea');
    t.value = text;
    document.body.appendChild(t);
    t.select();
    const ok = document.execCommand('copy');
    t.remove();
    if (!ok) { toast("Couldn't copy. Show the QR code instead."); return; }
  }
  toast('Copied, paste it in your group chat.');
  trackStep('group_share_completed', { code: s.code, role, how: 'copy' });
}
